import React from "react";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import { blue } from "@mui/material/colors";

const TermsOfService = () => {
  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Typography variant="h2" gutterBottom>
          Terms of Service
        </Typography>
        <Typography variant="body1" paragraph>
          <b>Updated:</b> July 18, 2023
        </Typography>
        <Typography variant="body1" paragraph>
          These Terms of Use apply when you use the services of Malayalam.ai
          (“Services”). The Terms include our Service Terms, Sharing &
          Publication Policy, Usage Policies, and other documentation,
          guidelines, or policies we may provide in writing. By using our
          Services, you agree to these Terms. Our Privacy Policy explains how we
          collect and use personal information.
        </Typography>
        <Typography variant="h4" gutterBottom>
          Registration and Access
        </Typography>
        <Typography variant="body1" paragraph>
          To use the Services provided by Malayalam.ai, you must be at least 13
          years old. If you are under 18, you must obtain permission from your
          parent or legal guardian to use the Services. If you are using the
          Services on behalf of another individual or entity, you must have the
          necessary authority to accept the Terms on their behalf. It is
          important to provide accurate and complete information during the
          registration process. You are prohibited from sharing your access
          credentials or account with individuals outside your organization. You
          are solely responsible for all activities conducted using your
          credentials.
        </Typography>
        <Typography variant="h4" gutterBottom>
          Usage Requirements
        </Typography>
        <Typography variant="body1" paragraph component="div">
          (a) <strong>Use of Services:</strong> You have the right to access and
          use the Services offered by Malayalam.ai in accordance with these
          Terms. When using the Services, you must comply with these Terms as
          well as all applicable laws. Malayalam.ai and its affiliates retain
          all rights, title, and interest in and to the Services.
          <br /> <br /> (b) <strong>Feedback:</strong> We greatly appreciate
          receiving feedback, comments, ideas, proposals, and suggestions for
          improving our Services. If you provide any of these contributions, we
          may use them without any restrictions or obligations to compensate
          you.
          <br /> <br /> (c) <strong>Restrictions:</strong> You are prohibited
          from engaging in the following activities: (i) using the Services in a
          manner that infringes upon, misappropriates, or violates the rights of
          others; (ii) attempting to reverse assemble, reverse compile,
          decompile, translate, or otherwise discover the source code or
          underlying components of the Services' models, algorithms, and systems
          (except to the extent that such restrictions are contrary to
          applicable law); (iii) using the output from the Services to develop
          models that directly compete with Malayalam.ai; (iv) using any
          automated or programmatic methods, except as permitted through the
          API, to extract data or output from the Services, including activities
          such as scraping, web harvesting, or web data extraction; (v)
          misrepresenting the output from the Services as being human-generated
          when it is not, or otherwise violating our Usage Policies; (vi)
          buying, selling, or transferring API keys without obtaining our prior
          consent; or (vii) sending us any personal information regarding
          children under 13 years old or under the applicable age of digital
          consent. You must also adhere to any rate limits and other
          requirements specified in our documentation. The use of the Services
          is limited to the geographies currently supported by Malayalam.ai.{" "}
          <br /> <br />
          (d) <strong>Third-Party Services:</strong> Any third-party software,
          services, or other products that you utilize in connection with the
          Services are subject to their own terms and conditions. Malayalam.ai
          is not responsible for any third-party products and services.
        </Typography>
        <Typography variant="h4" gutterBottom>
          Content
        </Typography>
        <Typography variant="body1" paragraph component="div">
          (a) <strong>User-generated Content:</strong> When using the Services
          provided by Malayalam.ai, you have the ability to input information
          ("Input") and receive generated output ("Output") based on that input.
          This Input and Output collectively form the "Content." As per
          applicable law and subject to your compliance with these Terms, you
          retain ownership of all Input. Malayalam.ai hereby assigns to you all
          its rights, title, and interest in the Output. This grants you the
          freedom to utilize the Content for various purposes, including
          commercial use, provided that you adhere to these Terms. Malayalam.ai
          may use the Content to deliver and maintain the Services, comply with
          applicable laws, and enforce our policies. It is your responsibility
          to ensure that the Content does not violate any applicable laws or
          these Terms.
          <br /> <br />
          (b) <strong>Similarity of Generated Content:</strong> Due to the
          nature of machine learning, the Output generated by the Services may
          not be exclusive to any particular user. It is possible for the
          Services to produce the same or similar Output for multiple users or
          even Malayalam.ai itself. For instance, if you submit an inquiry like
          "What color is the sky?" and receive the response "The sky is blue,"
          other users may receive the same answer when asking similar questions.
          In such cases, the responses generated for other users are not
          considered as your exclusive Content.
          <br /> <br />
          (c) <strong>Use of Content for Service Enhancement:</strong> The
          Content you provide or receive through our API ("API Content") is not
          utilized for the development or improvement of our Services. However,
          we may employ Content from sources other than our API ("Non-API
          Content") to aid in the development and enhancement of our Services.
          Further details regarding the use of Non-API Content to improve model
          performance can be found here. If you wish to opt out of allowing your
          Non-API Content to contribute to the improvement of our Services,
          please fill out this form. It's important to note that opting out may
          limit our ability to cater specifically to your use case.
          <br /> <br />
          (d) <strong>Accuracy Considerations:</strong> Artificial intelligence
          and machine learning are rapidly advancing fields of study. At
          Malayalam.ai, we continuously strive to enhance the accuracy,
          reliability, safety, and usefulness of our Services. However, due to
          the probabilistic nature of machine learning, there may be instances
          where the use of our Services produces inaccurate Output that does not
          precisely reflect real people, places, or factual information. It is
          essential for you to evaluate the accuracy of the Output based on your
          specific use case, which may include employing human review methods
          alongside the generated Output.
        </Typography>

        <Typography variant="h4" gutterBottom>
          Subscriptions
        </Typography>
        <Typography variant="body1" paragraph component="div">
          We appreciate your interest in subscribing to our premium product at
          Malayalam.ai. Before proceeding with your subscription, we would like
          to outline our Subscription Policy to ensure clarity and transparency
          regarding the terms and conditions.
          <br />
          <br />
          (a) <strong>Subscription and Billing Cycle:</strong> By subscribing to
          our premium product, you gain access to exclusive features and
          enhanced functionality. Upon subscribing, your subscription will be
          active until the end of the current billing cycle. The billing cycle
          is defined as the period for which you have made the payment,
          typically on a monthly or annual basis.
          <br />
          <br />
          (b) <strong>Cancellation of Subscription:</strong> We understand that
          circumstances may change, and you may decide to cancel your
          subscription at any time. Upon cancellation, your subscription will
          remain active until the end of the current billing cycle. At this
          time, no refunds are offered for the remaining duration of the billing
          cycle.
          <br />
          <br />
          (c) <strong>Free Trial:</strong> Prior to subscribing to our premium
          product, we provide users with the opportunity to explore and
          experience the features of our product through a free trial period
          with a generous number of free credits. This allows you to assess the
          suitability of our premium product before making a commitment to
          subscribe.
          <br />
          <br />
          (d) <strong>Refunds:</strong> As stated above, no refunds are provided
          for the cancellation of subscriptions during the billing cycle. This
          policy ensures that you have the opportunity to thoroughly evaluate
          the benefits and functionality of our premium product before
          subscribing.
          <br />
          <br />
          We highly value our relationship with our subscribers and strive to
          provide an exceptional experience. If you have any questions or
          concerns regarding our Subscription Policy, please contact our
          customer support team, and we will be happy to assist you.
        </Typography>
        <Typography variant="h4" gutterBottom>
          Indemnification; Disclaimer of Warranties; Limitations on Liability
        </Typography>
        <Typography variant="body1" paragraph component="div">
          (a) <strong>Indemnity:</strong> You agree to defend, indemnify, and
          hold harmless Malayalam.ai, our affiliates, and our personnel, from
          and against any claims, losses, and expenses (including attorneys’
          fees) arising from or relating to your use of the Services, including
          your Content, products or services you develop or offer in connection
          with the Services, and your breach of these Terms or violation of
          applicable law.
          <br /> <br />
          (b) <strong>Disclaimer:</strong> THE SERVICES ARE PROVIDED “AS IS.”
          EXCEPT TO THE EXTENT PROHIBITED BY LAW, MALAYALAM.AI AND OUR
          AFFILIATES AND LICENSORS MAKE NO WARRANTIES (EXPRESS, IMPLIED,
          STATUTORY, OR OTHERWISE) WITH RESPECT TO THE SERVICES, AND DISCLAIM
          ALL WARRANTIES INCLUDING BUT NOT LIMITED TO WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, SATISFACTORY
          QUALITY, NON-INFRINGEMENT, AND QUIET ENJOYMENT, AND ANY WARRANTIES
          ARISING OUT OF ANY COURSE OF DEALING OR TRADE USAGE. WE DO NOT WARRANT
          THAT THE SERVICES WILL BE UNINTERRUPTED, ACCURATE, OR ERROR-FREE, OR
          THAT ANY CONTENT WILL BE SECURE OR NOT LOST OR ALTERED.
          <br /> <br />
          (c) <strong>Limitations of Liability:</strong> NEITHER MALAYALAM.AI
          NOR ANY OF OUR AFFILIATES OR LICENSORS WILL BE LIABLE FOR ANY
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES,
          INCLUDING DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, OR DATA OR OTHER
          LOSSES, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH
          DAMAGES. OUR AGGREGATE LIABILITY UNDER THESE TERMS SHALL NOT EXCEED
          THE GREATER OF THE AMOUNT YOU PAID FOR THE SERVICE THAT GAVE RISE TO
          THE CLAIM DURING THE 12 MONTHS BEFORE THE LIABILITY AROSE OR ONE
          HUNDRED DOLLARS ($100). THE LIMITATIONS IN THIS SECTION APPLY ONLY TO
          THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.
        </Typography>
        <Typography variant="h4" gutterBottom>
          Term and Termination
        </Typography>
        <Typography variant="body1" paragraph component="div">
          (a) <strong>Termination; Suspension:</strong> These Terms become
          effective upon your initial use of the Services and remain in effect
          until terminated. You have the right to terminate these Terms at any
          time and for any reason by ceasing the use of the Services and
          Content. We reserve the right to terminate these Terms for any reason
          by providing you with at least 30 days' advance notice. Additionally,
          we may terminate these Terms immediately upon notifying you if you
          materially breach Sections 2 (Usage Requirements), 5 (Confidentiality,
          Security and Data Protection), 8 (Dispute Resolution), or 9 (General
          Terms), if there are changes in relationships with third-party
          technology providers beyond our control, or to comply with applicable
          law or government requests. In case of non-compliance with these
          Terms, if your use poses a security risk to us or any third party, or
          if we suspect fraudulent activity or potential liability to us or any
          third party, we may suspend your access to the Services.
          <br /> <br />
          (b) <strong>Effect on Termination:</strong> Upon termination, you are
          required to discontinue the use of the Services and promptly return
          or, if instructed by us, destroy any Confidential Information. The
          sections of these Terms that, by their nature, should survive
          termination or expiration will remain in effect, including but not
          limited to Sections 3 and 5-9.
        </Typography>
        <Typography variant="h4" gutterBottom>
          Dispute Resolution
        </Typography>
        <Typography variant="body1" paragraph component="div">
          (a) <strong>Mandatory Arbitration:</strong> You and Malayalam.ai agree
          to resolve any past or present claims relating to these Terms or our
          Services through final and binding arbitration, except that you have
          the right to opt out of these arbitration terms, and future changes to
          these arbitration terms, by filling out this form within 30 days of
          agreeing to these arbitration terms or the relevant changes.
          <br /> <br />
          (b) <strong>Informal Dispute Resolution:</strong> We aim to understand
          and address your concerns before resorting to formal legal action.
          Before filing a claim against Malayalam.ai, you agree to attempt
          resolving the dispute informally by notifying us at
          dispute-resolution@malayalam.ai. Your notice should include your name,
          a description of the dispute, and the relief you seek. If we are
          unable to resolve the dispute within 60 days, you may initiate a
          formal proceeding. The 60-day resolution process tolls any applicable
          statute of limitations. If you reside in the EU, you can access the
          European Commission's online dispute resolution platform at
          https://ec.europa.eu/consumers/odr.
          <br /> <br />
          (c) <strong>Arbitration Forum:</strong> Either party may commence
          binding arbitration through ADR Services, an alternative dispute
          resolution provider. The arbitration fees will be shared equally by
          both parties. If the arbitrator determines that you are unable to
          afford the arbitration fees and cannot obtain a waiver, Malayalam.ai
          will cover them on your behalf. Malayalam.ai will not seek its
          attorneys' fees and costs in arbitration unless the arbitrator deems
          your claim frivolous.
          <br /> <br />
          (d) <strong>Arbitration Procedures:</strong> The arbitration will be
          conducted by telephone, based on written submissions, video
          conference, or in person in San Francisco, California, or at another
          mutually agreed location. A sole arbitrator appointed by ADR Services,
          following their prevailing rules, will oversee the arbitration. The
          arbitrator has authority to decide all issues, except for a California
          court's jurisdiction in determining (i) the scope, enforceability, and
          arbitrability of this Section 8, including the mass filing procedures
          outlined below, and (ii) your compliance with the pre-arbitration
          requirements in this section. The settlement offer amount will not be
          disclosed to the arbitrator by either party until after the arbitrator
          determines the final award, if any.
          <br /> <br />
          (e) <strong>Exceptions:</strong> This arbitration section does not
          apply to (i) individual claims brought in small claims court, and (ii)
          claims seeking injunctive or other equitable relief to prevent
          unauthorized use or abuse of the Services or intellectual property
          infringement.
          <br /> <br />
          (f) <strong>No Class Actions:</strong> Disputes must be brought on an
          individual basis only and may not be brought as a plaintiff or class
          member in any purported class, consolidated, or representative
          proceeding. Class arbitrations, class actions, private attorney
          general actions, and consolidation with other arbitrations are not
          permitted. If, for any reason, a dispute proceeds in court instead of
          arbitration, both parties knowingly and irrevocably waive their right
          to a trial by jury in any action, proceeding, or counterclaim.
          However, this does not prevent either party from participating in a
          class-wide settlement of claims.
          <br /> <br />
          (g) <strong>Mass Filings:</strong> If, at any time, 30 or more similar
          demands for arbitration against Malayalam.ai or related parties are
          asserted by the same or coordinated counsel or entities ("Mass
          Filing"), ADR Services will assign sequential numbers to each of the
          Mass Filings randomly. Claims numbered 1-10 will be referred to as the
          "Initial Test Cases" and will proceed to arbitration first. The
          arbitrators will render a final award for the Initial Test Cases
          within 120 days of the initial pre-hearing conference, unless the
          claims are resolved beforehand or the parties agree to extend the
          deadline. Following this, the parties will have 90 days (the
          "Mediation Period") to resolve the remaining cases through mediation
          based on the awards from the Initial Test Cases. If the outstanding
          claims are not resolved during this period, the parties may choose to
          opt out of the arbitration process and proceed in court by providing
          written notice to the other party within 60 days after the Mediation
          Period. Otherwise, the remaining cases will be arbitrated in their
          assigned order. The statute of limitations will be tolled from the
          time the Initial Test Cases are chosen until your case is selected as
          described above.
          <br /> <br />
          (h) <strong>Severability:</strong> If any part of this Section 8 is
          found to be illegal or unenforceable, the remaining provisions will
          remain in effect, unless a finding of partial illegality or
          unenforceability would permit Mass Filing, class or representative
          arbitration. In such cases, this Section 8 will be deemed
          unenforceable in its entirety. Nothing in this section waives or
          limits the right to seek public injunctive relief or any other
          non-waivable right, pending a ruling on the substance of such claim
          from the arbitrator.
        </Typography>
      </Box>
    </Container>
  );
};

export default TermsOfService;
